/** Typed wrapper over the server's REST API. */
import type { Ciphertext, PreKeyBundle } from '@vav/protocol';

export interface UserSummary {
  id: string;
  username: string;
  displayName: string;
  createdAt: number;
  lastSeen?: number;
  online?: boolean;
}

export interface InviteSummary {
  id: string;
  username: string;
  displayName: string;
}

export interface InviteRecord {
  code: string;
  createdAt: number;
  expiresAt: number;
  uses: number;
  maxUses?: number;
}

export interface DeviceSummary {
  deviceId: number;
  identityKey: string;
  createdAt: number;
}

export interface GroupSummary {
  id: string;
  createdAt: number;
  members: { userId: string; role: string }[];
}

export interface OutgoingMessage {
  toUserId: string;
  toDeviceId?: number;
  groupId?: string;
  ciphertext: Ciphertext;
}

export class ApiError extends Error {
  status: number;
  code?: string;

  constructor(status: number, code: string | undefined, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export class Api {
  baseUrl: string;
  token?: string;

  constructor(baseUrl: string, token?: string) {
    this.baseUrl = baseUrl.replace(/\/$/, '');
    this.token = token;
  }

  async #request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const isForm = typeof FormData !== 'undefined' && init.body instanceof FormData;
    const res = await fetch(`${this.baseUrl}${path}`, {
      ...init,
      headers: {
        ...(init.body && !isForm ? { 'content-type': 'application/json' } : {}),
        ...(this.token ? { authorization: `Bearer ${this.token}` } : {}),
        ...init.headers,
      },
    });

    if (!res.ok) {
      let code: string | undefined;
      let detail = res.statusText;
      try {
        const body = (await res.json()) as { error?: string; message?: string };
        code = body.error;
        detail = body.message ?? body.error ?? detail;
      } catch {
        // Non-JSON error body (a proxy timeout page, say) — the status is enough.
      }
      throw new ApiError(res.status, code, `${init.method ?? 'GET'} ${path}: ${detail}`);
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  #post<T>(path: string, body: unknown): Promise<T> {
    return this.#request<T>(path, { method: 'POST', body: JSON.stringify(body) });
  }

  register(body: {
    username: string;
    displayName: string;
    deviceId: number;
    identityKey: string;
    registrationId: number;
    signedPreKey: { id: number; pub: string; sig: string };
    oneTimePreKeys: { id: number; pub: string }[];
    signature: string;
    inviteCode?: string;
  }) {
    return this.#post<{
      userId: string;
      deviceId: number;
      username: string;
      token: string;
      invitedBy?: InviteSummary;
    }>('/v1/auth/register', body);
  }

  challenge(username: string) {
    return this.#post<{ userId: string; nonce: string }>('/v1/auth/challenge', { username });
  }

  login(body: { userId: string; deviceId: number; nonce: string; signature: string }) {
    return this.#post<{ token: string; userId: string; deviceId: number }>('/v1/auth/login', body);
  }

  me() {
    return this.#request<UserSummary>('/v1/me');
  }

  searchUsers(q: string, phoneHash?: string) {
    const query = new URLSearchParams({ q });
    if (phoneHash) query.set('phoneHash', phoneHash);
    return this.#request<{ users: UserSummary[] }>(`/v1/users?${query}`);
  }

  getUser(userId: string) {
    return this.#request<UserSummary>(`/v1/users/${encodeURIComponent(userId)}`);
  }

  getKeys(userId: string) {
    return this.#request<{ bundles: PreKeyBundle[] }>(`/v1/keys/${encodeURIComponent(userId)}`);
  }

  getDeviceKeys(userId: string, deviceId: number) {
    return this.#request<{ bundles: PreKeyBundle[] }>(
      `/v1/keys/${encodeURIComponent(userId)}/${deviceId}`,
    );
  }

  /** Device list without consuming a one-time prekey. */
  /** Change the name shown to everyone. The username never moves. */
  updateProfile(displayName: string) {
    return this.#request<{ ok: true; displayName: string }>('/v1/me', {
      method: 'PATCH',
      body: JSON.stringify({ displayName }),
    });
  }

  /** Delete this account on the server. Irreversible, and nobody is told. */
  deleteAccount() {
    return this.#request<{ ok: true }>('/v1/me', { method: 'DELETE' });
  }

  listDevices(userId: string) {
    return this.#request<{ devices: DeviceSummary[] }>(
      `/v1/users/${encodeURIComponent(userId)}/devices`,
    );
  }

  registerDevice(body: {
    userId: string;
    identityKey: string;
    registrationId: number;
    signedPreKey: { id: number; pub: string; sig: string };
    oneTimePreKeys: { id: number; pub: string }[];
    nonce: string;
    signature: string;
  }) {
    return this.#post<{ deviceId: number; token: string }>('/v1/devices/register', body);
  }

  removeDevice(deviceId: number) {
    return this.#request<{ ok: true }>(`/v1/devices/${deviceId}`, { method: 'DELETE' });
  }

  // --- linking a new device -------------------------------------------------

  startProvision(ephemeralPub: string) {
    return this.#post<{ provisionId: string; expiresAt: number }>('/v1/devices/provision', {
      ephemeralPub,
    });
  }

  approveProvision(provisionId: string, body: { senderPub: string; ciphertext: string; ephemeralPub: string }) {
    return this.#post<{ ok: true }>(
      `/v1/devices/provision/${encodeURIComponent(provisionId)}/approve`,
      body,
    );
  }

  pollProvision(provisionId: string) {
    return this.#request<
      { pending: true } | { pending: false; userId: string; senderPub: string; ciphertext: string }
    >(`/v1/devices/provision/${encodeURIComponent(provisionId)}`);
  }

  keyCount() {
    return this.#request<{ count: number }>('/v1/keys/count');
  }

  uploadKeys(body: {
    signedPreKey?: { id: number; pub: string; sig: string };
    oneTimePreKeys?: { id: number; pub: string }[];
  }) {
    return this.#post<{ ok: true }>('/v1/keys', body);
  }

  sendMessages(messages: OutgoingMessage[], deliverAfter?: number, pushHint?: 'call' | 'silent') {
    return this.#post<{ accepted: { id: string; toUserId: string }[]; skipped?: string[]; serverTs: number }>(
      '/v1/messages',
      {
        messages,
        ...(deliverAfter === undefined ? {} : { deliverAfter }),
        ...(pushHint === undefined ? {} : { pushHint }),
      },
    );
  }

  /** Withdraw held envelopes before their time. Returns what was actually cancelled. */
  unschedule(ids: string[]) {
    return this.#post<{ cancelled: string[] }>('/v1/messages/unschedule', { ids });
  }

  createInvite(body: { maxUses?: number; ttlDays?: number } = {}) {
    return this.#post<{ code: string; expiresAt: number }>('/v1/invites', body);
  }

  resolveInvite(code: string) {
    return this.#request<{ inviter: InviteSummary; expiresAt: number }>(
      `/v1/invites/${encodeURIComponent(code)}`,
    );
  }

  listInvites() {
    return this.#request<{ invites: InviteRecord[] }>('/v1/invites');
  }

  revokeInvite(code: string) {
    return this.#request<{ ok: true }>(`/v1/invites/${encodeURIComponent(code)}`, {
      method: 'DELETE',
    });
  }

  discoveryConfig() {
    return this.#request<{ pepper: string; defaultRegion?: string; maxBatch: number }>(
      "/v1/discovery/config",
    );
  }

  setDiscoveryPhone(phoneHash: string | null) {
    return this.#request<{ ok: true }>("/v1/discovery/me", {
      method: "PUT",
      body: JSON.stringify({ phoneHash }),
    });
  }

  lookupPhones(hashes: string[]) {
    return this.#post<{
      matches: { phoneHash: string; userId: string; username: string; displayName: string }[];
    }>("/v1/discovery/lookup", { hashes });
  }

  pushConfig() {
    return this.#request<{ enabled: boolean; web: { vapidPublicKey: string } | null }>(
      '/v1/push/config',
    );
  }

  setPushToken(platform: 'expo' | 'web', token: string) {
    return this.#request<{ ok: true }>('/v1/push/token', {
      method: 'PUT',
      body: JSON.stringify({ platform, token }),
    });
  }

  clearPushToken() {
    return this.#request<{ ok: true }>('/v1/push/token', { method: 'DELETE' });
  }

  createGroup(memberIds: string[]) {
    return this.#post<{ groupId: string; memberIds: string[]; createdAt: number }>('/v1/groups', {
      memberIds,
    });
  }

  listGroups() {
    return this.#request<{ groups: GroupSummary[] }>('/v1/groups');
  }

  addGroupMembers(groupId: string, userIds: string[]) {
    return this.#post<{ ok: true }>(`/v1/groups/${encodeURIComponent(groupId)}/members`, { userIds });
  }

  removeGroupMember(groupId: string, userId: string) {
    return this.#request<{ ok: true }>(
      `/v1/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(userId)}`,
      { method: 'DELETE' },
    );
  }

  /** Short-lived ICE servers for a call; STUN-only when TURN is not set up. */
  iceServers() {
    return this.#request<{ iceServers: unknown[]; relay: boolean }>('/v1/calls/ice');
  }

  async uploadMedia(blob: Uint8Array): Promise<{ mediaId: string; size: number }> {
    const form = new FormData();
    // Copy into a fresh ArrayBuffer: a Uint8Array view over a larger buffer
    // would otherwise upload the whole backing store.
    form.append('file', new Blob([blob.slice().buffer as ArrayBuffer]), 'blob');
    return this.#request('/v1/media', { method: 'POST', body: form });
  }

  async downloadMedia(mediaId: string): Promise<Uint8Array> {
    const res = await fetch(`${this.baseUrl}/v1/media/${encodeURIComponent(mediaId)}`, {
      headers: this.token ? { authorization: `Bearer ${this.token}` } : {},
    });
    if (!res.ok) throw new ApiError(res.status, undefined, `media ${mediaId}: ${res.statusText}`);
    return new Uint8Array(await res.arrayBuffer());
  }
}
